// WebAuthn PRF-based authenticator enrollment + unlock.
//
// The authenticator is used strictly for its PRF output — a 32-byte,
// hardware-bound, stable (for a given credentialId + salt) secret. We
// wrap the device's master key under that secret with AES-GCM, giving
// us a "passkey envelope" structurally identical to the seed envelope.
//
// We do NOT use the WebAuthn assertion signature for server-side
// authentication. The user is already authenticated by other means
// (paper key, device pairing); passkeys here are a local-unlock
// mechanism, not a login credential. Challenges in the create/get
// ceremonies are random bytes generated client-side and discarded
// after the ceremony completes.
//
// Each browser is its own device (its own seed, its own master key),
// so each enrollment produces a fresh credentialId even when the
// underlying hardware is shared (e.g., Touch ID on two browsers of
// the same Mac). That aligns with the project's "Firefox and Chrome
// are different devices" model.

import { StoredAuthenticator } from './deviceStore'

// Fixed, public, domain-separated salt. Same salt + same credential =
// same PRF output, which is the stability property we need to unwrap
// the same envelope on every unlock.
const PRF_SALT_STRING = 'practicemd:master-key-wrap:v1'
let cachedSalt: Uint8Array | null = null

/** Exported for the passkey-restore spike page, which must evaluate the
 *  SAME salt the product uses so its fingerprints mean something. */
export async function prfSalt(): Promise<Uint8Array> {
    if (cachedSalt) return cachedSalt
    const data = new TextEncoder().encode(PRF_SALT_STRING)
    const digest = await crypto.subtle.digest('SHA-256', data)
    cachedSalt = new Uint8Array(digest)
    return cachedSalt
}

// Detects whether the browser exposes a platform authenticator at
// all. NOT a guarantee of PRF support — only an authenticator-level
// attempt can confirm that. The harness uses this to decide whether
// to offer the enrollment button in the first place.
export async function isPasskeyCapable(): Promise<boolean> {
    if (typeof window === 'undefined') return false
    if (!('PublicKeyCredential' in window)) return false
    try {
        return await (window as any).PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    } catch {
        return false
    }
}

// What the browser SAYS about the PRF extension, without prompting anyone.
// PublicKeyCredential.getClientCapabilities() (Chrome 133+, Safari 17.4+)
// reports 'extension:prf'; a browser without that call cannot be asked, and
// only an authenticator-level attempt settles it — which is what the spike
// page (/spike/passkey-restore) does. Release 1 branches on this: 'no'
// means the silent-device path, with the kit asked before the first reply.
export type PrfCapability = 'yes' | 'no' | 'unknown'

export interface ClientPasskeyCapabilities {
    prf: PrfCapability
    platformAuthenticator: boolean
    conditionalMediation: boolean
    /** The raw getClientCapabilities() map, when the browser has the call. */
    raw: Record<string, boolean> | null
}

/** Pure: the tri-state from a getClientCapabilities() map. An absent key
 *  means the client does not know, and says so. */
export function prfFromCapabilities(raw: Record<string, boolean> | null | undefined): PrfCapability {
    if (!raw) return 'unknown'
    const v = raw['extension:prf']
    if (v === true) return 'yes'
    if (v === false) return 'no'
    return 'unknown'
}

export async function passkeyCapabilities(): Promise<ClientPasskeyCapabilities> {
    const none: ClientPasskeyCapabilities = { prf: 'unknown', platformAuthenticator: false, conditionalMediation: false, raw: null }
    if (typeof window === 'undefined' || !('PublicKeyCredential' in window)) return none
    const PKC = (window as any).PublicKeyCredential
    let raw: Record<string, boolean> | null = null
    try {
        if (typeof PKC.getClientCapabilities === 'function') raw = await PKC.getClientCapabilities()
    } catch {
        raw = null
    }
    const platformAuthenticator = await isPasskeyCapable()
    let conditionalMediation = false
    try {
        if (typeof PKC.isConditionalMediationAvailable === 'function') {
            conditionalMediation = !!(await PKC.isConditionalMediationAvailable())
        }
    } catch {
        conditionalMediation = false
    }
    return { prf: prfFromCapabilities(raw), platformAuthenticator, conditionalMediation, raw }
}

// A ceremony the PERSON ended — declined the prompt, dismissed it, or let it
// time out. WebAuthn reports cancellation and timeout identically
// (NotAllowedError); an AbortController surfaces AbortError; and
// enrollRecoveryPasskey throws its own message when create() resolves null.
//
// Distinguishing this from a real failure is what lets the UI fall back to a
// passphrase instead of accusing someone of an error they chose.
export function isPasskeyCancellation(e: unknown): boolean {
    const name = (e as { name?: unknown } | null)?.name
    if (name === 'NotAllowedError' || name === 'AbortError') return true
    return e instanceof Error && e.message === 'credential creation was cancelled'
}

// The device HAS a platform authenticator but its passkey cannot carry an
// encryption secret (no PRF extension), so passkey unlock is impossible here
// no matter how willing the person is. isUserVerifyingPlatformAuthenticator-
// Available() cannot see this — it answers "is there an authenticator", not
// "can it do PRF" — so this only surfaces mid-ceremony.
export function isPasskeyUnusable(e: unknown): boolean {
    return e instanceof Error && e.message.includes('does not support the PRF extension')
}

// Runs the get ceremony against the stored authenticators, picks out
// the credential the user chose, and unwraps its envelope to recover
// the master key bytes. Caller is responsible for passing the bytes
// to the worker and zeroing its copy.
export async function unlockViaAuthenticator(
    authenticators: StoredAuthenticator[]
): Promise<Uint8Array> {
    if (!authenticators.length) throw new Error('no authenticators registered')
    const allowCredentialIds = authenticators.map(a => a.credentialId)
    const { credentialId, prfOutput } = await runGetCeremony({ allowCredentialIds })
    try {
        const match = authenticators.find(a => a.credentialId === credentialId)
        if (!match) {
            throw new Error(`no stored envelope for credentialId ${credentialId}`)
        }
        return await unwrapWithRawKey(prfOutput, match.envelopeB64)
    } finally {
        prfOutput.fill(0)
    }
}

// Runs navigator.credentials.get with the PRF extension. Returns the
// credentialId that was used (so the caller can pick the right
// envelope) and the 32-byte PRF output.
async function runGetCeremony(opts: {
    allowCredentialIds: string[]
}): Promise<{ credentialId: string; prfOutput: Uint8Array }> {
    const salt = await prfSalt()
    const challenge = crypto.getRandomValues(new Uint8Array(32))

    const getOptions: any = {
        publicKey: {
            challenge,
            userVerification: 'preferred',
            allowCredentials: opts.allowCredentialIds.map(id => ({
                type: 'public-key' as const,
                id: base64urlToBytes(id),
            })),
            extensions: {
                prf: { eval: { first: salt } },
            },
        },
    }

    const cred = (await navigator.credentials.get(getOptions)) as PublicKeyCredential | null
    if (!cred) throw new Error('authenticator ceremony was cancelled')

    const credentialId = bytesToBase64url(new Uint8Array(cred.rawId))
    const ext = (cred as any).getClientExtensionResults?.()
    const prf = ext?.prf
    if (!prf?.results?.first) {
        throw new Error('authenticator did not return a PRF output')
    }
    const full = new Uint8Array(prf.results.first as ArrayBuffer)
    if (full.length < 32) {
        full.fill(0)
        throw new Error(`PRF output too short: ${full.length} bytes`)
    }
    try {
        return { credentialId, prfOutput: full.slice(0, 32) }
    } finally {
        full.fill(0)
    }
}

// ---- Envelope crypto (AES-256-GCM, no KDF). Matches the Go-side
// seedEnvelope format so the worker could decrypt one of these too if
// ever needed. Format:
//   base64( JSON{ v:1, cipher:"aes-256-gcm", nonce, ct } )
// where `nonce` and `ct` are base64 (Go's default []byte JSON encoding).

export async function wrapWithRawKey(rawKey: Uint8Array, plaintext: Uint8Array): Promise<string> {
    if (rawKey.length !== 32) throw new Error(`rawKey must be 32 bytes, got ${rawKey.length}`)
    const key = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt'])
    const nonce = crypto.getRandomValues(new Uint8Array(12))
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, plaintext)
    const env = {
        v: 1,
        cipher: 'aes-256-gcm',
        nonce: bytesToBase64(nonce),
        ct: bytesToBase64(new Uint8Array(ct)),
    }
    return bytesToBase64(new TextEncoder().encode(JSON.stringify(env)))
}

async function unwrapWithRawKey(rawKey: Uint8Array, envelopeB64: string): Promise<Uint8Array> {
    const json = new TextDecoder().decode(base64ToBytes(envelopeB64))
    const env = JSON.parse(json)
    if (env.v !== 1) throw new Error(`unsupported envelope version ${env.v}`)
    if (env.cipher !== 'aes-256-gcm') throw new Error(`unsupported cipher ${env.cipher}`)
    const key = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['decrypt'])
    const nonce = base64ToBytes(env.nonce)
    const ct = base64ToBytes(env.ct)
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, ct)
    return new Uint8Array(pt)
}

// ---- Base helpers

function bytesToBase64(bytes: Uint8Array): string {
    let s = ''
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
    return btoa(s)
}

function base64ToBytes(b64: string): Uint8Array {
    const bin = atob(b64)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out
}

function bytesToBase64url(bytes: Uint8Array): string {
    return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64urlToBytes(s: string): Uint8Array {
    let b64 = s.replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) b64 += '='
    return base64ToBytes(b64)
}

// ---- Recovery passkeys (Release 1, Send-First-and-Credential-App.md §7)
//
// A RECOVERY passkey is enrolled with different parameters from a local-
// unlock one: user.id is the ACCOUNT's id (not this browser's seedKid), so
// the same person on a new browser can find it by discoverable assertion;
// rp.id is the site's host, spelled out; resident key and user verification
// are REQUIRED; and the ceremony's PRF output is handed BACK to the caller,
// because the caller needs it twice — as this browser's local unlock key
// (the raw output, today's wrap) and as the input to the wasm's recovery-key
// derivation (an HKDF the site never implements). The caller zeroes it.

export interface RecoveryEnrollOpts {
    /** The recovery device's seed kid (hex) — the WebAuthn user handle
     *  (Appendix C, C1). Unique per passkey, so no store ever replaces one
     *  of the account's passkeys with another and no exclusion list is
     *  needed; rotation is "add the new, revoke the old". The server never
     *  reads the handle — passkeys are looked up by credential id. */
    recoveryDeviceSeedKid: string
    /** The account's email: the credential's `name`, the field every
     *  manager shows as the user name. */
    email: string
    /** What the passkey is called (C2): the credential's `displayName`. */
    label: string
}

function hexToBytes(hex: string): Uint8Array {
    if (hex.length % 2 !== 0 || /[^0-9a-fA-F]/.test(hex)) throw new Error('hexToBytes: not hex')
    const out = new Uint8Array(hex.length / 2)
    for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
    return out
}

export interface RecoveryEnrollment {
    credentialId: string // base64url, as the browser reports rawId
    publicKey: string // COSE_Key from getPublicKey(), base64 (standard)
    transports?: string[]
    /** The 32-byte PRF output. Caller-owned: zero it when done. */
    prfOutput: Uint8Array
}

/** The relying party id every recovery passkey is bound to: this host. */
export function relyingPartyId(): string {
    return window.location.hostname
}

export async function enrollRecoveryPasskey(opts: RecoveryEnrollOpts): Promise<RecoveryEnrollment> {
    const salt = await prfSalt()
    const challenge = crypto.getRandomValues(new Uint8Array(32))
    const userHandle = hexToBytes(opts.recoveryDeviceSeedKid)
    if (userHandle.length === 0 || userHandle.length > 64) {
        throw new Error(`recovery device kid must be 1–64 bytes, got ${userHandle.length}`)
    }
    const createOptions: any = {
        publicKey: {
            challenge,
            rp: { name: 'PracticeMD', id: relyingPartyId() },
            user: {
                id: userHandle,
                name: opts.email,
                displayName: opts.label,
            },
            pubKeyCredParams: [
                { type: 'public-key', alg: -7 }, // ES256
                { type: 'public-key', alg: -257 }, // RS256
            ],
            authenticatorSelection: {
                residentKey: 'required',
                userVerification: 'required',
            },
            extensions: { prf: { eval: { first: salt } } },
            // No excludeCredentials: with a per-passkey user handle (C1) a
            // second ceremony on the same store mints a second passkey
            // rather than replacing this one, which is the point.
        },
    }

    const cred = (await navigator.credentials.create(createOptions)) as PublicKeyCredential | null
    if (!cred) throw new Error('credential creation was cancelled')
    const credentialId = bytesToBase64url(new Uint8Array(cred.rawId))
    const response = cred.response as AuthenticatorAttestationResponse
    // getPublicKey() would yield SPKI; the server verifies COSE. The
    // attestation object's authData carries the COSE key verbatim (WebAuthn
    // §6.5), so it is taken from there.
    const coseKey = extractCoseKeyFromAttestation(new Uint8Array(response.attestationObject))
    if (!coseKey) throw new Error('attestation carried no public key')
    const transports =
        typeof (response as any).getTransports === 'function' ? (response as any).getTransports() : undefined

    const ext = (cred as any).getClientExtensionResults?.()
    const prf = ext?.prf
    let prfOutputFull: Uint8Array | null = null
    if (prf?.results?.first) {
        prfOutputFull = new Uint8Array(prf.results.first as ArrayBuffer)
    } else if (prf?.enabled) {
        const got = await runGetCeremony({ allowCredentialIds: [credentialId] })
        prfOutputFull = got.prfOutput
    } else {
        throw new Error('authenticator does not support the PRF extension')
    }
    if (prfOutputFull.length < 32) {
        prfOutputFull.fill(0)
        throw new Error(`PRF output too short: ${prfOutputFull.length} bytes`)
    }
    const prfOutput = prfOutputFull.slice(0, 32)
    prfOutputFull.fill(0)
    return { credentialId, publicKey: bytesToBase64(coseKey), transports, prfOutput }
}

export interface RecoveryAssertion {
    credentialId: string // base64url
    clientDataJSON: string // base64url
    authenticatorData: string // base64url
    signature: string // base64url
    /** The 32-byte PRF output. Caller-owned: zero it when done. */
    prfOutput: Uint8Array
}

// A DISCOVERABLE assertion (empty allowCredentials): the person picks their
// Practice MD passkey from the password manager, on a browser that knows
// nothing about the account yet. The assertion goes to recoveryEnvelope;
// the PRF output derives the key that opens what comes back.
export async function assertForRecovery(): Promise<RecoveryAssertion> {
    const salt = await prfSalt()
    const challenge = crypto.getRandomValues(new Uint8Array(32))
    const getOptions: any = {
        publicKey: {
            challenge,
            rpId: relyingPartyId(),
            userVerification: 'required',
            allowCredentials: [],
            extensions: { prf: { eval: { first: salt } } },
        },
    }
    const cred = (await navigator.credentials.get(getOptions)) as PublicKeyCredential | null
    if (!cred) throw new Error('authenticator ceremony was cancelled')
    const response = cred.response as AuthenticatorAssertionResponse
    const ext = (cred as any).getClientExtensionResults?.()
    const prf = ext?.prf
    if (!prf?.results?.first) throw new Error('authenticator did not return a PRF output')
    const full = new Uint8Array(prf.results.first as ArrayBuffer)
    if (full.length < 32) {
        full.fill(0)
        throw new Error(`PRF output too short: ${full.length} bytes`)
    }
    const prfOutput = full.slice(0, 32)
    full.fill(0)
    return {
        credentialId: bytesToBase64url(new Uint8Array(cred.rawId)),
        clientDataJSON: bytesToBase64url(new Uint8Array(response.clientDataJSON)),
        authenticatorData: bytesToBase64url(new Uint8Array(response.authenticatorData)),
        signature: bytesToBase64url(new Uint8Array(response.signature)),
        prfOutput,
    }
}

export function bytesToHexString(bytes: Uint8Array): string {
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

// ---- Attestation parsing: the COSE public key out of attestationObject.
//
// attestationObject is CBOR {fmt, attStmt, authData}; authData is bytes:
// rpIdHash(32) flags(1) signCount(4) [aaguid(16) credIdLen(2) credId(n)
// COSE_Key(...)]. The COSE key is the tail of authData, so once the
// credential id is skipped, the rest IS the key — no CBOR key parsing
// needed here, only enough CBOR to find authData in the top-level map.
export function extractCoseKeyFromAttestation(attestationObject: Uint8Array): Uint8Array | null {
    const authData = cborMapBytesValue(attestationObject, 'authData')
    if (!authData || authData.length < 55) return null
    const flags = authData[32]
    if ((flags & 0x40) === 0) return null // no attested credential data
    const credIdLen = (authData[53] << 8) | authData[54]
    const start = 55 + credIdLen
    if (authData.length <= start) return null
    return authData.slice(start)
}

// Minimal CBOR: read the top-level map and return the byte-string value of
// one text key. Skips other values structurally. Enough for attestation
// objects from real authenticators; anything odd yields null.
function cborMapBytesValue(b: Uint8Array, wantKey: string): Uint8Array | null {
    let i = 0
    const head = (): [number, number] | null => {
        if (i >= b.length) return null
        const ib = b[i++]
        const major = ib >> 5
        const ai = ib & 0x1f
        if (ai < 24) return [major, ai]
        const n = 1 << (ai - 24)
        if (ai > 27 || i + n > b.length) return null
        let v = 0
        for (let k = 0; k < n; k++) v = v * 256 + b[i + k]
        i += n
        return [major, v]
    }
    const skip = (): boolean => {
        const h = head()
        if (!h) return false
        const [major, arg] = h
        switch (major) {
            case 0: case 1: case 7: return true
            case 2: case 3: i += arg; return i <= b.length
            case 4: for (let k = 0; k < arg; k++) if (!skip()) return false; return true
            case 5: for (let k = 0; k < arg; k++) if (!skip() || !skip()) return false; return true
            case 6: return skip()
            default: return false
        }
    }
    const h = head()
    if (!h || h[0] !== 5) return null
    const entries = h[1]
    for (let k = 0; k < entries; k++) {
        const kh = head()
        if (!kh) return null
        let key: string | null = null
        if (kh[0] === 3) {
            key = new TextDecoder().decode(b.slice(i, i + kh[1]))
            i += kh[1]
        } else if (kh[0] === 2) {
            i += kh[1]
        } else if (kh[0] !== 0 && kh[0] !== 1) {
            return null
        }
        if (key === wantKey) {
            const vh = head()
            if (!vh || vh[0] !== 2) return null
            return b.slice(i, i + vh[1])
        }
        if (!skip()) return null
    }
    return null
}
